import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Breadcrumbs,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  PriorityHigh as HighPriorityIcon,
  Schedule as PendingIcon,
  CheckCircle as ResolvedIcon,
  Cancel as ClosedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// API imports
import TicketsApiService from "../../../../infrastructure/api/tickets";
import DefaultApiService from "../../../../infrastructure/api/default";

const TicketsList = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [filters, setFilters] = useState({
    categoryId: "",
    status: "",
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    ticketCategoryId: "",
    priority: 2, // Medium
    tags: "",
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadCategories();
    loadTickets();
    if (isAdmin) loadStatistics();
  }, [isAdmin]);

  const loadCategories = async () => {
    try {
      // استفاده از endpoint دسته‌بندی‌های تیکت از UM/Category
      // این endpoint دسته‌بندی‌های TCK_Category را برمی‌گرداند
      const response = await DefaultApiService.getListApi("UM/Category");
      console.log("Categories response:", response);
      
      // getListApi برمی‌گرداند response.data که ApiResult است
      // ApiResult structure: { isSuccess: true, data: [...], ... }
      const apiData = response?.data || response;
      
      if (Array.isArray(apiData)) {
        setCategories(apiData);
      } else if (apiData && Array.isArray(apiData.data)) {
        setCategories(apiData.data);
      } else if (apiData && apiData.isSuccess && Array.isArray(apiData.data)) {
        setCategories(apiData.data);
      } else {
        console.warn("Unexpected categories response structure:", response);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories([]);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const cleanFilters = {};
      if (filters.categoryId) cleanFilters.categoryId = filters.categoryId;
      if (filters.status) cleanFilters.status = filters.status;

      const response = isAdmin
        ? await TicketsApiService.getAllTickets(cleanFilters)
        : await TicketsApiService.getUserTickets(cleanFilters);

      if (response && response.isSuccess) {
        setTickets(response.data || []);
      } else if (response && !response.isSuccess) {
        // فقط اگر response وجود داشت اما isSuccess false بود، toast نمایش می‌دهیم
        toast.error(response.message || "خطا در دریافت تیکت‌ها");
      }
    } catch (error) {
      // فقط اگر خطای شبکه یا خطای واقعی بود، toast نمایش می‌دهیم
      if (error.response?.status !== 404) {
        toast.error("خطا در ارتباط با سرور");
      }
      console.error("Error loading tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await TicketsApiService.getStatistics();
      if (response.isSuccess) {
        setStatistics(response.data || {});
      }
    } catch (error) {
      console.error("Error loading statistics:", error);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim() || !newTicket.ticketCategoryId) {
      toast.warning("تمام فیلدهای الزامی را پر کنید");
      return;
    }

    setCreating(true);
    try {
      const ticketData = {
        title: newTicket.title,
        description: newTicket.description,
        ticketCategoryId: parseInt(newTicket.ticketCategoryId),
        priority: parseInt(newTicket.priority),
        tags: newTicket.tags,
      };

      const response = await TicketsApiService.createTicket(ticketData);
      if (response.isSuccess) {
        toast.success("تیکت با موفقیت ایجاد شد");
        setCreateDialogOpen(false);
        setNewTicket({
          title: "",
          description: "",
          ticketCategoryId: "",
          priority: 2,
          tags: "",
        });
        loadTickets();
      } else {
        toast.error(response.message || "خطا در ایجاد تیکت");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error creating ticket:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/SLN/Tickets/Detail/${ticketId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fa-IR");
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return <PendingIcon color="warning" />;
      case "inprogress":
        return <PendingIcon color="info" />;
      case "resolved":
        return <ResolvedIcon color="success" />;
      case "closed":
        return <ClosedIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "warning";
      case "inprogress":
        return "info";
      case "resolved":
        return "success";
      case "closed":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority >= 4) return <HighPriorityIcon color="error" />;
    if (priority >= 3) return <HighPriorityIcon color="warning" />;
    return null;
  };

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Typography color="text.secondary">پشتیبانی</Typography>
        <Typography color="text.primary">
          {isAdmin ? "مدیریت تیکت‌ها" : "تیکت‌های من"}
        </Typography>
      </Breadcrumbs>

      {/* Statistics for Admin */}
      {isAdmin && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(statistics).map(([status, count]) => (
            <Grid item xs={6} md={3} key={status}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h4" color="primary">
                    {count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {status}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>دسته‌بندی</InputLabel>
                <Select
                  value={filters.categoryId}
                  onChange={(e) => {
                    setFilters({ ...filters, categoryId: e.target.value });
                    setTimeout(() => loadTickets(), 0);
                  }}
                  label="دسته‌بندی"
                >
                  <MenuItem value="">
                    <em>همه</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                    setTimeout(() => loadTickets(), 0);
                  }}
                  label="وضعیت"
                >
                  <MenuItem value="">
                    <em>همه</em>
                  </MenuItem>
                  <MenuItem value="1">باز</MenuItem>
                  <MenuItem value="2">در حال انجام</MenuItem>
                  <MenuItem value="3">در انتظار پاسخ کاربر</MenuItem>
                  <MenuItem value="4">حل شده</MenuItem>
                  <MenuItem value="5">بسته شده</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                onClick={() => loadTickets()}
                startIcon={<RefreshIcon />}
                disabled={loading}
                fullWidth
              >
                {loading ? "در حال بارگذاری..." : "بروزرسانی"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              لیست تیکت‌ها ({tickets.length} مورد)
            </Typography>
            {!isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                تیکت جدید
              </Button>
            )}
          </Box>

          {tickets.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                هیچ تیکیتی یافت نشد
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>عنوان</TableCell>
                    <TableCell>دسته‌بندی</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>اولویت</TableCell>
                    <TableCell>تاریخ ایجاد</TableCell>
                    <TableCell>آخرین پاسخ</TableCell>
                    {isAdmin && <TableCell>کاربر</TableCell>}
                    <TableCell>پیام‌های خوانده نشده</TableCell>
                    <TableCell>عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {ticket.title}
                          </Typography>
                          {!ticket.isRead && (
                            <Chip
                              label="جدید"
                              size="small"
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{ticket.ticketCategoryTitle}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(ticket.statusTitle)}
                          <Chip
                            label={ticket.statusTitle}
                            color={getStatusColor(ticket.statusTitle)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getPriorityIcon(ticket.priority)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {ticket.priorityTitle}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(ticket.createOn)}</TableCell>
                      <TableCell>{formatDate(ticket.lastReplyDate)}</TableCell>
                      {isAdmin && <TableCell>{ticket.userName}</TableCell>}
                      <TableCell>
                        {ticket.unreadMessagesCount > 0 && (
                          <Chip
                            label={ticket.unreadMessagesCount}
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="مشاهده تیکت">
                          <IconButton
                            onClick={() => handleViewTicket(ticket.id)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>ایجاد تیکت جدید</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="عنوان تیکت *"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>دسته‌بندی *</InputLabel>
                <Select
                  value={newTicket.ticketCategoryId}
                  onChange={(e) => setNewTicket({ ...newTicket, ticketCategoryId: e.target.value })}
                  label="دسته‌بندی *"
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>اولویت</InputLabel>
                <Select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  label="اولویت"
                >
                  <MenuItem value={1}>کم</MenuItem>
                  <MenuItem value={2}>متوسط</MenuItem>
                  <MenuItem value={3}>بالا</MenuItem>
                  <MenuItem value={4}>فوری</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="توضیحات *"
                multiline
                rows={4}
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="برچسب‌ها (اختیاری)"
                value={newTicket.tags}
                onChange={(e) => setNewTicket({ ...newTicket, tags: e.target.value })}
                helperText="برچسب‌ها را با کاما جدا کنید"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>انصراف</Button>
          <Button
            onClick={handleCreateTicket}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? "در حال ایجاد..." : "ایجاد تیکت"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketsList;